/**
 * Panel oturumu, okul seçimi (KK-801), nokta süzgeci ve tema — Zustand.
 *
 * =============================================================================
 * KK-801: "tek ekrandan okul değiştirme"
 * -----------------------------------------------------------------------------
 * Personel JWT'si TEK BİR OKULA bağlıdır (okul_id token'in içindedir, ADR-18 /
 * RLS). Bu yüzden "okul değiştirme" sunucuda bir uç değil, İSTEMCİDE BİR
 * OTURUM SEÇİMİDİR: müdür her okul için bir kez giriş yapar, panel bu
 * oturumları yan yana tutar ve üst bardan tek tıklamayla aralarında geçer.
 * Oturum ve token tek kalıcı kayıtta tutulur. API istemcileri bu sekmenin
 * aktif oturumunu kullanır; başka sekmenin okul değişimi belleğe yansımadan
 * yeni okulun token'ıyla istek gönderilmez.
 *
 * Okul değiştiğinde TanStack Query önbelleği kabuk tarafından temizlenir —
 * A okulunun cirosu B okulunun ekranında bir an bile görünmemelidir.
 * =============================================================================
 */
import { create } from "zustand";
import { ROL_ADI, Rol } from "@kantin/shared";

export type Tema = "sistem" | "acik" | "koyu";

export interface PersonelOzeti {
  id: string;
  ad: string;
  soyad: string;
  rol: number;
  rol_adi: string;
  noktalar: string[];
  tum_noktalar: boolean;
}

export interface Oturum {
  okulId: string;
  okulAd: string;
  okulKodu: string;
  token: string;
  personel: PersonelOzeti;
  platform?: { kullaniciId: string; kullaniciAd: string };
}

interface PlatformJwt {
  tip: "platform";
  platformKullaniciId: string;
  okulId: string;
  okulAd: string;
  teknikPersonelId: string;
  ad: string;
}

/** Platform panelinden gelen 30 dakikalık okul-kapsamlı JWT'yi çalışma alanına ekler. */
export function platformOturumunuAl(token: string): void {
  const parca = token.split(".")[1];
  if (!parca) throw new Error("Platform erişim tokenı geçersiz.");
  const yuk = JSON.parse(
    decodeURIComponent(
      Array.from(atob(parca.replace(/-/g, "+").replace(/_/g, "/")))
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    ),
  ) as PlatformJwt;
  if (yuk.tip !== "platform") throw new Error("Platform erişim tokenı geçersiz.");
  oturumDurumu.getState().oturumEkle({
    okulId: yuk.okulId,
    okulAd: yuk.okulAd,
    okulKodu: "PLATFORM",
    token,
    personel: {
      id: yuk.teknikPersonelId,
      ad: yuk.ad,
      soyad: "",
      rol: Rol.OKUL_SISTEM_YONETICISI,
      rol_adi: ROL_ADI[Rol.OKUL_SISTEM_YONETICISI]!,
      noktalar: [],
      tum_noktalar: true,
    },
    platform: { kullaniciId: yuk.platformKullaniciId, kullaniciAd: yuk.ad },
  });
}

interface KaliciDurum {
  oturumlar: Oturum[];
  aktifOkulId: string | null;
  tema: Tema;
}

export interface OturumDurumu extends KaliciDurum {
  /** Üst bardaki nokta süzgeci; null = tüm noktalar */
  seciliNoktaId: string | null;

  oturumEkle(o: Oturum): void;
  okulSec(okulId: string): void;
  /** Token verilirse yalnızca hâlâ aynı olan oturumu kapatır (gecikmiş 401). */
  cikis(token?: string): void;
  noktaSec(noktaId: string | null): void;
  temaAyarla(tema: Tema): void;
}

const DEPO_ANAHTARI = "kantin.yonetim.oturum";

function kaliciOku(): KaliciDurum {
  const bos: KaliciDurum = { oturumlar: [], aktifOkulId: null, tema: "sistem" };
  try {
    const ham = localStorage.getItem(DEPO_ANAHTARI);
    if (!ham) return bos;
    const cozulen = JSON.parse(ham) as Partial<KaliciDurum>;
    return {
      oturumlar: Array.isArray(cozulen.oturumlar) ? cozulen.oturumlar : [],
      aktifOkulId: cozulen.aktifOkulId ?? null,
      tema: cozulen.tema ?? "sistem",
    };
  } catch {
    return bos;
  }
}

function kaliciYaz(d: KaliciDurum): void {
  try {
    localStorage.setItem(DEPO_ANAHTARI, JSON.stringify(d));
  } catch {
    /* depolama kapalıysa oturum yalnızca bu sekmede yaşar */
  }
}

const baslangic = kaliciOku();

export const oturumDurumu = create<OturumDurumu>((set, get) => ({
  ...baslangic,
  seciliNoktaId: null,

  oturumEkle: (o) =>
    set((s) => {
      // Aynı okula ikinci kez giriş yapılırsa kayıt TAZELENİR, çoğaltılmaz.
      const oturumlar = [
        ...s.oturumlar.filter((x) => x.okulId !== o.okulId),
        o,
      ];
      const yeni: KaliciDurum = {
        oturumlar,
        aktifOkulId: o.okulId,
        tema: s.tema,
      };
      kaliciYaz(yeni);
      return { ...yeni, seciliNoktaId: null };
    }),

  okulSec: (okulId) =>
    set((s) => {
      if (!s.oturumlar.some((o) => o.okulId === okulId)) return {};
      const yeni: KaliciDurum = {
        oturumlar: s.oturumlar,
        aktifOkulId: okulId,
        tema: s.tema,
      };
      kaliciYaz(yeni);
      // Nokta süzgeci okula özeldir; okul değişince sıfırlanır.
      return { ...yeni, seciliNoktaId: null };
    }),

  cikis: (token) =>
    set((s) => {
      if (token !== undefined) {
        if (aktifOturum(s)?.token !== token) return s;
        const kalici = kaliciOku();
        const kaliciAktif = kalici.oturumlar.find((o) => o.okulId === kalici.aktifOkulId);
        // Diğer sekmenin yeni girişi storage olayı işlenmeden kaydedilmiş olabilir.
        if (kaliciAktif?.token !== token) {
          // Depo temizlendiyse veya erişilemiyorsa eski oturumu yeniden yazma.
          // Yeni giriş varsa storage olayını beklemeden bu sekmeyi de eşle.
          return { ...kalici, seciliNoktaId: null };
        }
      }
      const oturumlar = s.oturumlar.filter((o) => o.okulId !== s.aktifOkulId);
      const yeni: KaliciDurum = {
        oturumlar,
        aktifOkulId: oturumlar[0]?.okulId ?? null,
        tema: s.tema,
      };
      kaliciYaz(yeni);
      return { ...yeni, seciliNoktaId: null };
    }),

  noktaSec: (seciliNoktaId) => set({ seciliNoktaId }),

  temaAyarla: (tema) => {
    const s = get();
    const yeni: KaliciDurum = {
      oturumlar: s.oturumlar,
      aktifOkulId: s.aktifOkulId,
      tema,
    };
    kaliciYaz(yeni);
    set({ tema });
  },
}));

/** Aktif oturum — yoksa null (giriş ekranı gösterilir). */
export function aktifOturum(d: OturumDurumu): Oturum | null {
  return d.oturumlar.find((o) => o.okulId === d.aktifOkulId) ?? null;
}

/**
 * §5.1 RBAC matrisi İSTEMCİDE UYGULANMAZ; karar her zaman sunucudadır
 * (personelGerekli). Bu kanca yalnızca UX içindir: sunucunun kesin olarak
 * reddedeceği bir yüzeyi açık göstermek, kullanıcıya formu doldurttuktan
 * sonra "bu işlem için yetkiniz yok" demekle sonuçlanır — hata kullanıcıya
 * KENDİ hatası gibi görünür. Yetkisiz rol, okuma yetkisi varsa ekranı GÖRÜR;
 * yalnızca yazma düğmeleri kapanır.
 */
export function useYetkili(...roller: number[]): boolean {
  const oturum = aktifOturum(oturumDurumu());
  return oturum !== null && roller.includes(oturum.personel.rol);
}

/**
 * Temayı kök elemana yazar. `data-tema` KULLANICI SEÇİMİDİR ve sistem
 * tercihini EZER (tasarım sistemi §3); "sistem" seçildiğinde öznitelik
 * kaldırılır ve prefers-color-scheme yeniden yetkili olur.
 */
export function temayiUygula(tema: Tema): void {
  const kok = document.documentElement;
  if (tema === "sistem") delete kok.dataset.tema;
  else kok.dataset.tema = tema;
}

/**
 * Başka sekmedeki giriş, okul değişimi ve çıkış bellekteki oturuma yansır.
 * Aksi halde API yeni tokenı kullanırken menü ve düzenleme yetkileri eski
 * personel rolünde kalır. Oturum değişince sorgu önbelleği de temizlenir.
 */
export function baskaSekmeDinle(
  degisince: () => void,
): () => void {
  const isle = (o: StorageEvent) => {
    if (o.storageArea !== localStorage) return;
    if (o.key === DEPO_ANAHTARI || o.key === null) {
      const yeni = kaliciOku();
      const onceki = aktifOturum(oturumDurumu.getState());
      const aktif = yeni.oturumlar.find((oturum) => oturum.okulId === yeni.aktifOkulId);
      const degisti = onceki?.token !== aktif?.token || onceki?.okulId !== aktif?.okulId;
      if (degisti) degisince();
      // Depoya tekrar yazma: sekmeler arasında storage olayı döngüsü oluşmasın.
      oturumDurumu.setState({
        ...yeni,
        ...(degisti ? { seciliNoktaId: null } : {}),
      });
    }
  };
  window.addEventListener("storage", isle);
  return () => window.removeEventListener("storage", isle);
}
